"use client";

import type { TRUSTED_HOME_PAGE_QUERY_RESULT } from "@homeinstead/schemas";

import { usePathname, useSearchParams } from "next/navigation";
import { Hero } from "~care/components/app/landing/Hero";
import CareGiverLeadForm from "~care/components/ui/LeadForm/CareGiverLeadForm/CareGiverLeadForm";
import CareSeekerNationalLeadForm from "~care/components/ui/LeadForm/CareSeekerShallowLeadForm/CareSeekerNationalLeadForm";
import { LeadFormDrawer } from "~care/components/ui/LeadForm/LeadFormDrawer";
import { useIsDebug } from "~care/hooks/useIsDebug";
import { useMultiStepForm } from "~care/hooks/useMultiStepForm";
import { handleDrawerClose } from "~care/lib/utils/route-utils";

// Note that the displayed step and the actual step is off by 1 because the first step is hidden.
// Also, the final step is a Thank You page.
const INITIAL_STEP = 2;
const FINAL_STEP = 6;

export const NationalHero = ({
  className,
  pageData,
}: {
  className?: string;
  pageData: TRUSTED_HOME_PAGE_QUERY_RESULT;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { isDebug } = useIsDebug();

  const careSeekerShallowForm = useMultiStepForm({
    finalStep: FINAL_STEP,
    initialStep: INITIAL_STEP,
  });

  // Use the client search params for checking lead variant
  const careGiverForm = useMultiStepForm({
    finalStep: 2,
    initialStep: 2,
    onFirst: () => handleDrawerClose({ pathname, searchParams }),
  });

  const isCareGiverDrawerOpen = searchParams.get("leadVariant") === "CareGiver";

  if (!pageData) {
    return null;
  }

  return (
    <Hero className={className}>
      <CareSeekerNationalLeadForm
        className="xl:w-full"
        currentStep={careSeekerShallowForm.currentStep}
        finalStep={FINAL_STEP}
        handleBack={careSeekerShallowForm.prevStep}
        handleNext={careSeekerShallowForm.nextStep}
        isDebug={isDebug}
        leadSource="PPC"
        subtitle={pageData.subtitle}
        title={pageData.title}
      />
      <LeadFormDrawer
        description="Complete these steps to find care."
        hasBackButton
        isDrawerOpen={isCareGiverDrawerOpen} // Use careGiverForm's prevStep for the drawer's back button
        onBack={careGiverForm.prevStep}
        onClose={() => handleDrawerClose({ pathname, searchParams })} // This could also come from context/pageData if dynamic
        title="Get Care for a Loved One" // Ditto
      >
        {isCareGiverDrawerOpen && (
          <CareGiverLeadForm
            handleNext={careGiverForm.nextStep}
            isLastStep={careGiverForm.isLastStep}
          />
        )}
      </LeadFormDrawer>
    </Hero>
  );
};
